'use client'

// His vending machine, for visitors (28 Oct). Sam: "they can click the machine and they get a camera zoomed in view of
// the machine so they can see whats in it. I want users to be able to buy prints and sticker packs - and this goes
// through his shop. the prints will be chosen at random...we should have a cool reveal on this too so it feels gamey",
// and later the same day: "lets get it so they can buy things for Pino too ... somethiing to be careful about here is
// the payment flow, so we don't take people away from the page if we can help it".
//
// Walk up to it and a button offers the close view: the camera goes to the glass (VendingCam), and a panel says what
// is in each row as the town has it (/api/vending), sold out and stuck included, and what is waiting in the tray. A
// visitor can buy a print at random or a pack of six stickers for themselves, or tap a row and buy it for him, at twice
// its label, into the tray (lib/print-shop/vend.js). The till is Stripe's embedded checkout, mounted here in the panel
// when the site has a publishable key, so nobody leaves the page; without one it is Stripe's own page, and the buyer
// comes back to /?vend=<session>. Either way the drop plays once the webhook has drawn what dropped (VendingDrop: the
// thing itself, turning on a stage, the way a PlayStation game handed you one).

import { useCallback, useEffect, useRef, useState } from 'react'
import useGameStore from '../../stores/gameStore'
import { authFetch } from '../auth/useUser'
import { vendingSpot } from '../../lib/world/vending'
import { ink } from '../../lib/ui/theme'
import { VendingDrop } from './VendingDrop'
import { VendingPanel } from './VendingPanel'

const NEAR = 6
const POLL_MS = 450
const ORDER_MS = 2000
const ORDER_FOR_MS = 120_000
const KEY = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || ''


// Stripe.js from Stripe, once: it has to be loaded from js.stripe.com, never bundled
let stripeJs = null
function loadStripe() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'))
  stripeJs ||= new Promise((resolve, reject) => {
    if (window.Stripe) { resolve(window.Stripe(KEY)); return }
    const s = document.createElement('script')
    s.src = 'https://js.stripe.com/v3/'
    s.async = true
    s.onload = () => (window.Stripe ? resolve(window.Stripe(KEY)) : reject(new Error('no Stripe')))
    s.onerror = () => { stripeJs = null; reject(new Error('the till would not load')) }
    document.head.appendChild(s)
  })
  return stripeJs
}

// Stripe keeps one embedded checkout on a page at a time: opening and closing take turns, so a remount (React's
// own, in development) never asks for a second before the first is gone
let turns = Promise.resolve()
let mounted = null
const inTurn = (fn) => { const p = turns.then(fn); turns = p.catch(() => {}); return p }

// the till, in the panel: Stripe's own form in its own frame, our page round it
function Till({ secret, onComplete, onFail }) {
  const box = useRef(null)
  useEffect(() => {
    let dead = false
    inTurn(async () => {
      if (dead) return
      const stripe = await loadStripe()
      if (dead) return
      const c = await stripe.initEmbeddedCheckout({ fetchClientSecret: () => Promise.resolve(secret), onComplete })
      if (dead || !box.current) { c.destroy(); return }
      mounted = c
      c.mount(box.current)
    }).catch((err) => { if (!dead) onFail(err?.message || 'the till would not open') })
    return () => {
      dead = true
      inTurn(() => { try { mounted?.destroy() } catch { /* gone */ } mounted = null })
    }
  }, [secret, onComplete, onFail])
  return <div ref={box} style={{ minHeight: 360, background: '#fff', borderRadius: 10, overflow: 'hidden' }} />
}

export function VendingHud() {
  const [spot] = useState(() => (typeof window === 'undefined' ? null : vendingSpot()))
  const [near, setNear] = useState(false)
  const open = useGameStore((s) => s.vendingOpen)
  const setOpen = useGameStore((s) => s.setVendingOpen)
  const [data, setData] = useState(null)
  const [busy, setBusy] = useState('')
  const [said, setSaid] = useState('')
  const [picked, setPicked] = useState(null) // a row, to buy for him
  const [till, setTill] = useState(null) // { secret, id }
  const [drop, setDrop] = useState(null) // { session, status, result }
  const timer = useRef(null)

  // near it, the walker's way (ActionsHud): his stand spot, a few metres
  useEffect(() => {
    if (!spot) return
    const t = setInterval(() => {
      const pos = useGameStore.getState().playerPosGetter?.()
      if (!pos) { setNear(false); return }
      setNear(Math.hypot(pos.x - spot.stand[0], pos.z - spot.stand[2]) < NEAR)
    }, POLL_MS)
    return () => clearInterval(t)
  }, [spot])

  const read = useCallback(() => {
    fetch('/api/vending', { cache: 'no-store' }).then((r) => r.json()).then(setData).catch(() => setData({ on: false, rows: [] }))
  }, [])

  const close = useCallback(() => {
    clearTimeout(timer.current)
    setOpen(false)
    setDrop(null)
    setTill(null)
    setPicked(null)
    setSaid('')
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('vend')) {
      const u = new URL(window.location.href)
      u.searchParams.delete('vend')
      window.history.replaceState(null, '', u.toString())
    }
  }, [setOpen])

  const look = useCallback(() => {
    try { document.exitPointerLock?.() } catch { /* not locked */ }
    setOpen(true)
    read()
  }, [read, setOpen])

  // what dropped for a session: the spiral turns while the webhook draws it
  const watch = useCallback((session) => {
    clearTimeout(timer.current)
    setDrop({ session, status: 'turning' })
    const started = Date.now()
    const ask = () => fetch(`/api/vend/order?session=${encodeURIComponent(session)}`, { cache: 'no-store' })
      .then((r) => r.json())
      .then((o) => {
        if (o.status === 'ready' || o.status === 'sorry') { setDrop({ session, status: o.status, result: o }); read(); return }
        if (Date.now() - started > ORDER_FOR_MS) { setDrop({ session, status: 'slow' }); return }
        timer.current = setTimeout(ask, ORDER_MS)
      })
      .catch(() => { timer.current = setTimeout(ask, ORDER_MS) })
    timer.current = setTimeout(ask, 1400)
  }, [read])

  // back from Stripe's own page (no key for the one in the panel): the view opens on the drop
  useEffect(() => {
    const session = new URLSearchParams(window.location.search).get('vend')
    if (!session) return
    if (session === 'cancel') { setSaid('nothing bought. the machine keeps its things.'); return }
    if (!/^cs_/.test(session)) return
    setOpen(true)
    read()
    watch(session)
    return () => clearTimeout(timer.current)
  }, [read, setOpen, watch])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') { if (till) setTill(null); else close() } }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, close, till])

  const buy = useCallback(async (kind, code = null) => {
    setBusy(code || kind)
    setSaid('')
    try {
      const r = await authFetch('/api/vend/checkout', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kind, ...(code ? { code } : {}) }) })
      const d = await r.json()
      if (d.clientSecret && KEY) { setTill({ secret: d.clientSecret, id: d.id }); setBusy(''); return }
      if (d.url) { window.location.href = d.url; return }
      setSaid(d.error || 'the till jammed, try again')
    } catch {
      setSaid('the till jammed, try again')
    }
    setBusy('')
  }, [])

  const paid = useCallback(() => {
    const id = till?.id
    setTill(null)
    setPicked(null)
    if (id) watch(id)
  }, [till, watch])
  const tillFailed = useCallback((why) => { setTill(null); setSaid(why) }, [])

  if (!spot && !drop) return null

  if (!open) {
    if (!near && !said) return null
    return (
      <div style={{ position: 'fixed', left: '50%', bottom: 132, transform: 'translateX(-50%)', zIndex: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        {said && <div style={{ color: ink.dim, fontSize: 13 }}>{said}</div>}
        {near && (
          <button onClick={look} style={{ background: ink.bg, color: ink.text, border: `2px solid ${ink.line}`, borderRadius: 999, padding: '10px 18px', fontWeight: 700, cursor: 'pointer' }}>
            Look in the vending machine
          </button>
        )}
      </div>
    )
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, pointerEvents: 'none' }}>
      {drop ? (
        <VendingDrop drop={drop} onClose={close} />
      ) : (
        <VendingPanel
          data={data} busy={busy} said={said} onBuy={buy} onClose={close} onBack={() => setTill(null)}
          till={till ? <Till secret={till.secret} onComplete={paid} onFail={tillFailed} /> : null}
        />
      )}
    </div>
  )
}
